import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList } from 'shared/utils/Helpers';

export const AmusementDeviceDesignDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    deviceName: savedDeviceName = '',
    deviceClass: savedDeviceClass = '',
    operatingPower: savedOperatingPower = '',
    otherOperatingPower: savedOtherOperatingPower = '',
    mobilityType: savedMobilityType = '',
    maxSpeed: savedMaxSpeed = '',
    maxAcceleration: savedMaxAcceleration = '',
    maxResolvingSpeed: savedMaxResolvingSpeed = '',
    maxPatronSupportUnits: savedMaxPatronSupportUnits = '',
    maxAdultPatronsPerSupport: savedMaxAdultPatronsPerSupport = '',
    maxChildPatronsPerSupport: savedMaxChildPatronsPerSupport = '',
    minAgeUnaccompaniedPatron: savedMinAgeUnaccompaniedPatron = '',
    minAgeAccompaniedPatron: savedMinAgeAccompaniedPatron = '',
    maxHeightAttainedByPatron: savedMaxHeightAttainedByPatron = '',
    minPatronHeight: savedMinPatronHeight = '',
    electricalInstallation: savedElectricalInstallation = '',
    intrinsicSafety: savedIntrinsicSafety = '',
  } = value;

  const [deviceName, setDeviceName] = useState(savedDeviceName);
  const [deviceClass, setDeviceClass] = useState(savedDeviceClass);
  const [mobilityType, setMobilityType] = useState(savedMobilityType);
  const [operatingPower, setOperatingPower] = useState(savedOperatingPower);
  const [otherOperatingPower, setOtherOperatingPower] = useState(savedOtherOperatingPower);
  const [maxSpeed, setMaxSpeed] = useState(savedMaxSpeed);
  const [maxAcceleration, setMaxAcceleration] = useState(savedMaxAcceleration);
  const [maxResolvingSpeed, setMaxResolvingSpeed] = useState(savedMaxResolvingSpeed);
  const [maxPatronSupportUnits, setMaxPatronSupportUnits] = useState(savedMaxPatronSupportUnits);
  const [maxAdultPatronsPerSupport, setMaxAdultPatronsPerSupport] = useState(savedMaxAdultPatronsPerSupport);
  const [maxChildPatronsPerSupport, setMaxChildPatronsPerSupport] = useState(savedMaxChildPatronsPerSupport);
  const [minAgeUnaccompaniedPatron, setMinAgeUnaccompaniedPatron] = useState(savedMinAgeUnaccompaniedPatron);
  const [minAgeAccompaniedPatron, setMinAgeAccompaniedPatron] = useState(savedMinAgeAccompaniedPatron);
  const [maxHeightAttainedByPatron, setMaxHeightAttainedByPatron] = useState(savedMaxHeightAttainedByPatron);
  const [minPatronHeight, setMinPatronHeight] = useState(savedMinPatronHeight);
  const [electricalInstallation, setElectricalInstallation] = useState(savedElectricalInstallation);
  const [intrinsicSafety, setIntrinsicSafety] = useState(savedIntrinsicSafety);

  const REF_CONSTANTS = PlantDesignRefData.amusementDevice;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.amusementDevice;

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const deviceClassOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.deviceClassOptions]);
  const mobilityTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.mobilityTypeOptions]);
  const operatingPowerOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.operatingPowerOptions]);
  // TODO: const electricalInstallationOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.electricalInstallationOptions]);
  const electricalInstallationOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];
  const intrinsicSafetyOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.intrinsicSafetyOptions]);

  const validators = [
    {
      fieldName: 'deviceName',
      fieldValue: deviceName,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'deviceClass',
      fieldValue: deviceClass,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'mobilityType',
      fieldValue: mobilityType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'operatingPower',
      fieldValue: operatingPower,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'otherOperatingPower',
      fieldValue: otherOperatingPower,
      isValid: (v) => !isEmpty(v) || operatingPower !== REF_CONSTANTS.otherOperatingPower,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxSpeed',
      fieldValue: maxSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxAcceleration',
      fieldValue: maxAcceleration,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxResolvingSpeed',
      fieldValue: maxResolvingSpeed,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxPatronSupportUnits',
      fieldValue: maxPatronSupportUnits,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxAdultPatronsPerSupport',
      fieldValue: maxAdultPatronsPerSupport,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxChildPatronsPerSupport',
      fieldValue: maxChildPatronsPerSupport,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'minAgeUnaccompaniedPatron',
      fieldValue: minAgeUnaccompaniedPatron,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'minAgeAccompaniedPatron',
      fieldValue: minAgeAccompaniedPatron,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxHeightAttainedByPatron',
      fieldValue: maxHeightAttainedByPatron,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'minPatronHeight',
      fieldValue: minPatronHeight,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'electricalInstallation',
      fieldValue: electricalInstallation,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'intrinsicSafety',
      fieldValue: intrinsicSafety,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      deviceName,
      deviceClass,
      mobilityType,
      operatingPower,
      otherOperatingPower,
      maxSpeed,
      maxAcceleration,
      maxResolvingSpeed,
      maxPatronSupportUnits,
      maxAdultPatronsPerSupport,
      maxChildPatronsPerSupport,
      minAgeUnaccompaniedPatron,
      minAgeAccompaniedPatron,
      maxHeightAttainedByPatron,
      minPatronHeight,
      electricalInstallation,
      intrinsicSafety,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    deviceName,
    deviceClass,
    mobilityType,
    operatingPower,
    otherOperatingPower,
    maxSpeed,
    maxAcceleration,
    maxResolvingSpeed,
    maxPatronSupportUnits,
    maxAdultPatronsPerSupport,
    maxChildPatronsPerSupport,
    minAgeUnaccompaniedPatron,
    minAgeAccompaniedPatron,
    maxHeightAttainedByPatron,
    minPatronHeight,
    electricalInstallation,
    intrinsicSafety,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormGroup
        id="deviceName"
        label={PAGE_CONSTANTS.deviceName.label}
        isRequired
        hasError={showErrors && errors?.deviceName?.invalid}
        errorMessage={errors?.deviceName?.errorMessage}
      >
        <Input onChange={(e) => setDeviceName(e.target.value)} value={deviceName} />
      </FormGroup>

      <FormRadioGroup
        id="deviceClass"
        name="deviceClass"
        legend={PAGE_CONSTANTS.deviceClass.label}
        helpMessage={PAGE_CONSTANTS.deviceClassHelpMesssage}
        options={deviceClassOptions}
        value={deviceClass}
        onChange={setDeviceClass}
        vertical={true}
        hasError={showErrors && errors?.deviceClass?.invalid}
        errorMessage={errors?.deviceClass?.errorMessage}
        margin={{ top: 'lg' }}
      />

      <FormRadioGroup
        id="mobilityType"
        name="mobilityType"
        legend={PAGE_CONSTANTS.mobilityType.label}
        options={mobilityTypeOptions}
        value={mobilityType}
        onChange={setMobilityType}
        vertical={true}
        hasError={showErrors && errors?.mobilityType?.invalid}
        errorMessage={errors?.mobilityType?.errorMessage}
        margin={{ top: 'lg' }}
      />

      <RadioOrDropdownSelector
        name="operatingPower"
        options={operatingPowerOptions}
        title={PAGE_CONSTANTS.operatingPower.label}
        errorMessage={errors?.operatingPower?.errorMessage}
        hasError={showErrors && errors?.operatingPower?.invalid}
        onChange={(v) => {
          setOperatingPower(v);
          setOtherOperatingPower('');
        }}
        value={operatingPower}
        showExtraInput={operatingPower === REF_CONSTANTS.otherOperatingPower}
        onChangeExtraInput={setOtherOperatingPower}
        extraInputValue={otherOperatingPower}
        extraInputErrorMessage={errors?.otherOperatingPower?.errorMessage}
        extraInputHasError={showErrors && errors?.otherOperatingPower?.invalid}
        extraInputTitle={PAGE_CONSTANTS.otherOperatingPower.label}
        margin={{ top: 'lg' }}
      />

      <FormGroup
        id="maxSpeed"
        label={PAGE_CONSTANTS.maxSpeed.label}
        isRequired
        errorMessage={errors?.maxSpeed?.errorMessage}
        hasError={showErrors && errors?.maxSpeed?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxSpeed.suffix}
          onChange={(e) => setMaxSpeed(e.target.value)}
          value={maxSpeed}
          inputWidth="lg"
        />
      </FormGroup>

      <FormGroup
        id="maxAcceleration"
        label={PAGE_CONSTANTS.maxAcceleration.label}
        isRequired
        errorMessage={errors?.maxAcceleration?.errorMessage}
        hasError={showErrors && errors?.maxAcceleration?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxAcceleration.suffix}
          onChange={(e) => setMaxAcceleration(e.target.value)}
          value={maxAcceleration}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxResolvingSpeed"
        label={PAGE_CONSTANTS.maxResolvingSpeed.label}
        isRequired
        errorMessage={errors?.maxResolvingSpeed?.errorMessage}
        hasError={showErrors && errors?.maxResolvingSpeed?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxResolvingSpeed.suffix}
          onChange={(e) => setMaxResolvingSpeed(e.target.value)}
          value={maxResolvingSpeed}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxPatronSupportUnits"
        label={PAGE_CONSTANTS.maxPatronSupportUnits.label}
        isRequired
        errorMessage={errors?.maxPatronSupportUnits?.errorMessage}
        hasError={showErrors && errors?.maxPatronSupportUnits?.invalid}
        margin={{ top: 'lg' }}
      >
        <Input
          onChange={(e) => {
            setMaxPatronSupportUnits(e.target.value);
          }}
          value={maxPatronSupportUnits}
          inputWidth="sm"
        />
      </FormGroup>

      <FormGroup
        id="maxAdultPatronsPerSupport"
        label={PAGE_CONSTANTS.maxAdultPatronsPerSupport.label}
        isRequired
        errorMessage={errors?.maxAdultPatronsPerSupport?.errorMessage}
        hasError={showErrors && errors?.maxAdultPatronsPerSupport?.invalid}
        margin={{ top: 'lg' }}
      >
        <Input
          onChange={(e) => {
            setMaxAdultPatronsPerSupport(e.target.value);
          }}
          value={maxAdultPatronsPerSupport}
          inputWidth="sm"
        />
      </FormGroup>

      <FormGroup
        id="maxChildPatronsPerSupport"
        label={PAGE_CONSTANTS.maxChildPatronsPerSupport.label}
        isRequired
        errorMessage={errors?.maxChildPatronsPerSupport?.errorMessage}
        hasError={showErrors && errors?.maxChildPatronsPerSupport?.invalid}
        margin={{ top: 'lg' }}
      >
        <Input
          onChange={(e) => {
            setMaxChildPatronsPerSupport(e.target.value);
          }}
          value={maxChildPatronsPerSupport}
          inputWidth="sm"
        />
      </FormGroup>

      <FormGroup
        id="minAgeUnaccompaniedPatron"
        label={PAGE_CONSTANTS.minAgeUnaccompaniedPatron.label}
        isRequired
        errorMessage={errors?.minAgeUnaccompaniedPatron?.errorMessage}
        hasError={showErrors && errors?.minAgeUnaccompaniedPatron?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.minAgeUnaccompaniedPatron.suffix}
          onChange={(e) => setMinAgeUnaccompaniedPatron(e.target.value)}
          value={minAgeUnaccompaniedPatron}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="minAgeAccompaniedPatron"
        label={PAGE_CONSTANTS.minAgeAccompaniedPatron.label}
        isRequired
        errorMessage={errors?.minAgeAccompaniedPatron?.errorMessage}
        hasError={showErrors && errors?.minAgeAccompaniedPatron?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.minAgeAccompaniedPatron.suffix}
          onChange={(e) => setMinAgeAccompaniedPatron(e.target.value)}
          value={minAgeAccompaniedPatron}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxHeightAttainedByPatron"
        label={PAGE_CONSTANTS.maxHeightAttainedByPatron.label}
        isRequired
        errorMessage={errors?.maxHeightAttainedByPatron?.errorMessage}
        hasError={showErrors && errors?.maxHeightAttainedByPatron?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxHeightAttainedByPatron.suffix}
          onChange={(e) => setMaxHeightAttainedByPatron(e.target.value)}
          value={maxHeightAttainedByPatron}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="minPatronHeight"
        label={PAGE_CONSTANTS.minPatronHeight.label}
        isRequired
        errorMessage={errors?.minPatronHeight?.errorMessage}
        hasError={showErrors && errors?.minPatronHeight?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.minPatronHeight.suffix}
          onChange={(e) => setMinPatronHeight(e.target.value)}
          value={minPatronHeight}
          inputWidth="md"
        />
      </FormGroup>

      <FormRadioGroup
        id="electricalInstallation"
        name="electricalInstallation"
        legend={PAGE_CONSTANTS.electricalInstallation.label}
        options={electricalInstallationOptions}
        value={electricalInstallation}
        onChange={setElectricalInstallation}
        vertical={true}
        hasError={showErrors && errors?.electricalInstallation?.invalid}
        errorMessage={errors?.electricalInstallation?.errorMessage}
        margin={{ top: 'lg' }}
      />

      <FormRadioGroup
        id="intrinsicSafety"
        name="intrinsicSafety"
        legend={PAGE_CONSTANTS.intrinsicSafety.label}
        options={intrinsicSafetyOptions}
        value={intrinsicSafety}
        onChange={setIntrinsicSafety}
        vertical={true}
        hasError={showErrors && errors?.intrinsicSafety?.invalid}
        errorMessage={errors?.intrinsicSafety?.errorMessage}
        margin={{ top: 'lg' }}
      />
    </>
  );
});

AmusementDeviceDesignDetails.displayName = 'AmusementDeviceDesignDetails';

AmusementDeviceDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
